import { SafeHtml } from '@angular/platform-browser';

/**
 * HelpTag is now a string type to allow portal-specific tag enums.
 * Each portal should define its own enum for type safety (e.g., PublicHelpTag, ConfigHelpTag)
 */
export type HelpTag = string;

export enum HelpConditional {
    Users = 'Users',
    Redirect = 'Redirect',
    NotRedirect = 'NotRedirect',
    Fis = 'Fis',
    Tenant = 'Tenant',
    TOL = 'TellerOnlineLite',
    ScheduledPayments = 'ScheduledPayments'
}

export interface HelpArticleProperties {
    tag: HelpTag;
    bookmark: string;
    title: string;
    body: string;
    order: number;
    group?: string;
    conditions?: HelpConditional[];
    tenants?: string[];
}

export interface HelpConditionContext {
    tenantName: string;
    processorName: string;
    isRedirect: boolean;
    authenticatedUsersEnabled: boolean;
    tellerOnlineLiteEnabled: boolean;
    scheduledPaymentsEnabled: boolean;
}

export class HelpArticle {
    tag: HelpTag;

    bookmark: string;

    conditions?: HelpConditional[];

    title: string;

    body: string;

    trustedBody: SafeHtml;

    order: number;

    group?: string;

    tenants?: string[];

    constructor(properties: HelpArticleProperties) {
        this.tag = properties.tag;
        this.conditions = properties.conditions;
        this.tenants = properties.tenants;
        this.bookmark = properties.bookmark;
        this.title = properties.title;
        this.body = properties.body;
        this.order = properties.order;
        this.group = properties.group;
    }

    applyInlineConditions(context: HelpConditionContext): void {
        const regex = /\|condition:(?<condition>.*?)\|/;
        let match;
        while (match = regex.exec(this.body)) {
            const condition = match.groups!.condition as HelpConditional;

            // If the condition doesn't match, we want to remove the whole block
            const remove = !this.checkCondition(condition, context);

            // remove the whole block of text
            if (remove) {
                const replace: RegExp = new RegExp(`\\|condition:${condition}\\|.*?\\|endcondition:${condition}\\|`, 'g');
                this.body = this.body.replace(replace, '');
            } else {
                // remove just the conditional, leave the block of text
                this.body = this.body.replace(`|condition:${condition}|`, '').replace(`|endcondition:${condition}|`, '');
            }
        }
    }

    checkCondition(condition: HelpConditional, context: HelpConditionContext): boolean {
        switch (condition) {
            case HelpConditional.NotRedirect:
                return !context.isRedirect;
            case HelpConditional.Redirect:
                return context.isRedirect;
            case HelpConditional.Fis:
                return context.processorName.toLowerCase() === 'fis';
            case HelpConditional.Tenant:
                return this.tenants?.some(t => t.toLowerCase() === context.tenantName.toLowerCase()) ?? false;
            case HelpConditional.Users:
                return context.authenticatedUsersEnabled;
            case HelpConditional.TOL:
                return context.tellerOnlineLiteEnabled;
            case HelpConditional.ScheduledPayments:
                return context.scheduledPaymentsEnabled;
            default:
                return false;
        }
    }

    checkConditions(context: HelpConditionContext): boolean {
        return !this.conditions || this.conditions.every(condition => this.checkCondition(condition, context));
    }

    /** Replaces :icon-name: placeholders with inline Material font icon markup.
     * Article bodies are rendered via innerHTML, so the icon must be plain markup
     * (a font span) rather than an Angular component.
     */
    replaceIcons(): void {
        const regex = /[^https]:(?<icon>[^\s]*?):[^\/]/;
        let match;
        while (match = regex.exec(this.body)) {
            const { icon } = (match.groups!);
            this.body = this.body.replace(
                `:${icon}:`,
                `<span class="material-symbols-rounded" aria-hidden="true">${icon}</span>`);
        }
    }

    match(matchValue: string, tagMatch: boolean = false): boolean {
        return tagMatch
            ? this.tag === matchValue
            : this.body.includes(matchValue) || this.title.includes(matchValue) || this.tag.includes(matchValue);
    }
}
